import { useEffect, useState } from "react";
import styles from "./ScrollArrows.module.css";

/**
 * Previous and next buttons for a row that scrolls sideways elsewhere on the page, so a
 * swipeable row reads as one before anyone swipes it. `target` is the row's id; a press
 * moves it one card. Each button goes quiet at its end of the row. `label` names what the
 * row holds ("analyses" → "Next analyses"). The page hides them where the row doesn't scroll.
 */
export default function ScrollArrows({ target, label, className = "" }) {
  const [ends, setEnds] = useState({ start: true, end: false });

  useEffect(() => {
    const row = document.getElementById(target);
    if (!row) return undefined;
    const update = () => {
      const max = row.scrollWidth - row.clientWidth;
      setEnds({ start: row.scrollLeft <= 1, end: row.scrollLeft >= max - 1 });
    };
    row.addEventListener("scroll", update, { passive: true });
    // Also reports once as soon as it starts observing, which sets the first state.
    const ro = new ResizeObserver(update);
    ro.observe(row);
    return () => {
      row.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [target]);

  const step = (direction) => {
    const row = document.getElementById(target);
    if (!row || (direction < 0 ? ends.start : ends.end)) return;
    const [a, b] = row.children;
    const by = b ? b.offsetLeft - a.offsetLeft : row.clientWidth;
    const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    row.scrollBy({ left: direction * by, behavior: smooth ? "smooth" : "auto" });
  };

  // aria-disabled rather than disabled: a keyboard user pressing Next to the end keeps
  // focus on the button instead of dropping back to the page.
  return (
    <div className={`${styles.arrows} ${className}`.trim()}>
      <button
        type="button"
        className={styles.arrow}
        aria-label={`Previous ${label}`}
        aria-controls={target}
        aria-disabled={ends.start ? "true" : undefined}
        onClick={() => step(-1)}
      >
        <span aria-hidden="true">&#8592;</span>
      </button>
      <button
        type="button"
        className={styles.arrow}
        aria-label={`Next ${label}`}
        aria-controls={target}
        aria-disabled={ends.end ? "true" : undefined}
        onClick={() => step(1)}
      >
        <span aria-hidden="true">&#8594;</span>
      </button>
    </div>
  );
}
